/**
 * TenantContext - Global account context for permission resolution
 * 
 * MA-ARCH-201 Phase 3: Explicit account context for permission system.
 * MA-PHASE11A: Extended with account switching for all users.
 * MA-PHASE11A-P0-FRONTEND-ACCOUNT-CONTEXT-HEADER-001: Persist selected account for API interceptor.
 * 
 * - Regular users: Can switch between their accessible accounts
 * - SuperAdmins: Can override via ?account_id=... URL parameter
 * - Source of truth: URL param for SuperAdmin, state for regular users
 * - API interceptor: Reads selected account from localStorage to attach X-Account-ID header
 * 
 * CRITICAL: Permission resolution requires EXPLICIT account_id.
 * No implicit account selection happens on backend.
 */
import React, { createContext, useContext, useCallback, useMemo, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { getStoredUser, setSelectedAccountId, getSelectedAccountId } from "@/lib/auth";
import { api } from "@/lib/api";

const TenantContext = createContext({
  selectedTenantId: null,
  setSelectedTenantId: () => {},
  isViewingOtherTenant: false,
  isSuperAdmin: false,
  homeTenantId: null,
  clearTenantOverride: () => {},
  loading: false,
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: Account metadata
  accounts: [],
  currentAccount: null,
  currentAccountName: "",
  refreshAccounts: () => {},
});

export function TenantProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [defaultAccountId, setDefaultAccountId] = useState(null);
  // P4-WP-3: seed from the persisted selection so an Account switch survives a
  // refresh. Before WP-3 this started as null on every mount, so a non-
  // SuperAdmin's switch silently reverted to their default Account. The value
  // is only a CLIENT preference -- the backend revalidates eligibility on every
  // Account-scoped request, and an ineligible selection is dropped below.
  const [selectedAccountOverride, setSelectedAccountOverride] = useState(
    () => getSelectedAccountId() || null
  );
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]); // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001
  const [accountsLoading, setAccountsLoading] = useState(true); // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001
  const hasSetUrlParam = React.useRef(false); // MA-PHASE11A: Track if we've set URL param to prevent flicker
  const user = getStoredUser();
  
  // MA-ARCH-201 Phase 3: Check SuperAdmin via platform_role
  const isSuperAdmin = user?.platform_role === "super_admin";
  
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: Fetch accounts list (single source of truth)
  // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Dedupe fetch to prevent double calls
  const accountsFetchInFlightRef = useRef(false);
  const hasFetchedRef = useRef(false);
  
  const fetchAccounts = useCallback(async (force = false) => {
    // Skip if already fetching or already fetched (unless forced)
    if (accountsFetchInFlightRef.current) return;
    if (!force && hasFetchedRef.current) return;
    if (!user) {
      setAccountsLoading(false);
      return;
    }
    
    accountsFetchInFlightRef.current = true;
    
    try {
      const response = await api.get('/auth/me/accounts');
      const accountsList = response.data.accounts || [];
      setAccounts(accountsList);
      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setAccountsLoading(false);
      accountsFetchInFlightRef.current = false;
    }
  }, [user?.id]);
  
  // Fetch accounts on mount (only once)
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);
  
  // MA-ARCH-201 Phase 3: Fetch user's default account from backend
  // This establishes EXPLICIT account context for regular users
  useEffect(() => {
    async function fetchDefaultAccount() {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        // Call backend to get user's first active membership
        // This is the EXPLICIT account context for this user
        const response = await api.get('/auth/me/default-account');
        if (response.data?.account_id) {
          setDefaultAccountId(response.data.account_id);
        }
      } catch (error) {
        console.error('Failed to fetch default account:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchDefaultAccount();
  }, [user?.id]);
  
  // Read account_id from URL (for SuperAdmin override)
  const urlAccountId = searchParams.get("account_id") || searchParams.get("tenant_id"); // Support both params
  
  // MA-PHASE11A: EXPLICIT account context resolution with switching support
  // MA-PHASE11A-BUGFIX-001: Enhanced logging for state tracking
  // - SuperAdmins: Use URL param if present, otherwise default
  // - Non-SuperAdmins: Use override if set (from account switching), otherwise default
  const selectedTenantId = useMemo(() => {
    let result;
    if (!isSuperAdmin) {
      // Regular users: use override (from switching) or default
      result = selectedAccountOverride || defaultAccountId;
      // MA-PHASE11A: Debug logging commented out for production
      // console.log('[Tenant] Resolution (regular user):', {
      //   override: selectedAccountOverride,
      //   default: defaultAccountId,
      //   selected: result
      // });
    } else {
      // SuperAdmin: URL param takes precedence, fallback to default
      result = urlAccountId || defaultAccountId;
      // MA-PHASE11A: Debug logging commented out
      // console.log('[Tenant] Resolution (super_admin):', {
      //   urlParam: urlAccountId,
      //   defaultAccount: defaultAccountId,
      //   selected: result
      // });
    }
    return result;
  }, [isSuperAdmin, urlAccountId, defaultAccountId, selectedAccountOverride]);
  
  // MA-PHASE11A-P0-FRONTEND-ACCOUNT-CONTEXT-HEADER-001:
  // Persist selected account to localStorage so API interceptor can attach X-Account-ID header
  useEffect(() => {
    if (selectedTenantId) {
      setSelectedAccountId(selectedTenantId);
      // console.log('[Tenant] Persisted account ID for API interceptor:', selectedTenantId);
    }
  }, [selectedTenantId]);
  
  // Check if viewing a different account
  const isViewingOtherTenant = useMemo(() => {
    if (!isSuperAdmin) return false;
    return selectedTenantId && selectedTenantId !== defaultAccountId;
  }, [isSuperAdmin, selectedTenantId, defaultAccountId]);
  
  // MA-ARCH-201 Phase 3: SuperAdmins should have account_id in URL for determinism
  // MA-PHASE11A FIX: Only SuperAdmins use URL params, prevent flicker with ref tracking
  useEffect(() => {
    if (!isSuperAdmin || !defaultAccountId || hasSetUrlParam.current) return;
    
    // SuperAdmins only: If no account_id in URL, set it to default (ONE TIME)
    if (!urlAccountId) {
      hasSetUrlParam.current = true;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("account_id", defaultAccountId);
        return next;
      }, { replace: true });
    }
  }, [isSuperAdmin, defaultAccountId, urlAccountId, setSearchParams]);
  
  // MA-PHASE11A: Set account ID for ALL users
  // SuperAdmins: Update URL param
  // Regular users: Update override state
  // MA-SESSION-CACHE-ISOLATION-117B: Invalidate old account cache on switch
  const setSelectedTenantId = useCallback((newAccountId) => {
    const effectiveAccountId = newAccountId || defaultAccountId;
    
    // MA-PHASE11A DEBUG: Log account switch
    // console.log('[AccountSwitch] BEFORE - selectedTenantId:', selectedTenantId);
    // console.log('[AccountSwitch] switching to:', effectiveAccountId);
    
    // MA-SESSION-CACHE-ISOLATION-117B: Clear old account's cached queries
    // This prevents stale data when switching accounts
    if (selectedTenantId && selectedTenantId !== effectiveAccountId) {
      // Only import queryClient when needed (avoid circular dependencies)
      import('@/lib/queryClient').then(({ queryClient }) => {
        // Invalidate all queries that include the old account ID
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key.includes(selectedTenantId);
          }
        });
      });
    }
    
    if (isSuperAdmin) {
      // SuperAdmins: Use URL param for deterministic context
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (effectiveAccountId) {
          next.set("account_id", effectiveAccountId);
        }
        return next;
      }, { replace: true });
    } else {
      // Regular users: Update override state to trigger context change
      setSelectedAccountOverride(effectiveAccountId);
    }
  }, [isSuperAdmin, defaultAccountId, selectedTenantId, setSearchParams]);
  
  // Clear account override (return to default account)
  const clearTenantOverride = useCallback(() => {
    if (isSuperAdmin) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (defaultAccountId) {
          next.set("account_id", defaultAccountId);
        }
        return next;
      }, { replace: true });
    } else {
      // Regular users: Clear override
      setSelectedAccountOverride(null);
    }
  }, [isSuperAdmin, defaultAccountId, setSearchParams]);
  
  // MA-PHASE11A: Debug logging commented out for production
  // useEffect(() => {
  //   console.log("[AccountSwitch] AFTER - selectedTenantId:", selectedTenantId);
  //   console.debug("[TenantContext - Phase 11A]", {
  //     isSuperAdmin,
  //     defaultAccountId,
  //     selectedAccountOverride,
  //     selectedTenantId,
  //     isViewingOtherTenant,
  //     loading,
  //   });
  // }, [isSuperAdmin, defaultAccountId, selectedAccountOverride, selectedTenantId, isViewingOtherTenant, loading]);
  
  // P4-WP-3: reject a stale persisted selection.
  //
  // /auth/me/accounts returns exactly the Accounts the caller holds an active
  // membership in. Once it has loaded, a persisted selection outside that list
  // is no longer eligible -- the membership was removed, deactivated, or the
  // Account went away. Drop it and fall back to the default Account rather than
  // leaving the user pinned to an Account every request will now refuse.
  //
  // SuperAdmins are exempt: platform authority lets them select an Account they
  // hold no membership in, so absence from this list proves nothing about them.
  useEffect(() => {
    if (accountsLoading || isSuperAdmin) return;
    if (!selectedAccountOverride) return;
    if (accounts.length === 0) return;

    const stillEligible = accounts.some(
      (a) => a.account_id === selectedAccountOverride
    );
    if (!stillEligible) {
      console.warn(
        "[Tenant] Persisted account selection is no longer eligible; " +
          "falling back to the default account."
      );
      setSelectedAccountOverride(null);
      setSelectedAccountId(defaultAccountId || null);
    }
  }, [
    accountsLoading,
    isSuperAdmin,
    selectedAccountOverride,
    accounts,
    defaultAccountId,
  ]);

  // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: Derive current account from accounts list
  const currentAccount = useMemo(() => {
    return accounts.find(a => a.account_id === selectedTenantId) || null;
  }, [accounts, selectedTenantId]);
  
  const currentAccountName = useMemo(() => {
    return currentAccount?.account_name || "";
  }, [currentAccount]);
  
  const value = useMemo(() => ({
    selectedTenantId,
    setSelectedTenantId,
    isViewingOtherTenant,
    isSuperAdmin,
    homeTenantId: defaultAccountId, // Renamed for compatibility
    clearTenantOverride,
    loading: loading || accountsLoading, // Combined loading state
    // MA-TENANTCONTEXT-ACCOUNT-METADATA-CONSOLIDATION-001: Expose account metadata
    accounts,
    currentAccount,
    currentAccountName,
    refreshAccounts: () => fetchAccounts(true), // MA-TENANTCONTEXT-ACCOUNT-METADATA-DUPLICATE-FETCH-FIX-002: Force refresh
  }), [
    selectedTenantId, 
    setSelectedTenantId, 
    isViewingOtherTenant, 
    isSuperAdmin, 
    defaultAccountId, 
    clearTenantOverride, 
    loading, 
    accountsLoading,
    accounts,
    currentAccount,
    currentAccountName,
    fetchAccounts,
  ]);
  
  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

export function buildTenantPath(path, accountId) {
  /**
   * MA-ARCH-201 Phase 3: Build URL with explicit account context.
   * For SuperAdmins viewing other accounts.
   */
  if (!accountId) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}account_id=${accountId}`;
}

export default TenantContext;
